import { BaseApiService } from "api/BaseApiService";
import { SchoolTicket } from "common/components/cms/callForFootball/formMatchApplication";
import { BaseFilters, BaseResponce } from "common/interfaces/common";
import {
  IAmbassadorOneResponse,
  IAvailableSchool,
  ILevel,
  IMatchApplications,
  IMatchesApplication,
  IMutationPoints,
  IOrganizations,
  IPointResponse,
  ISeason,
  ISchoolDivision,
  ISchoolDivisionFilters,
  ISchoolDivisionSchool,
  IUpdateNotification,
  MatchApplicationFiltersType,
  PointsType,
  SchoolGathering,
} from "../types/BattleOfSchoolsTypes";

class BattleOfSchoolsRepository extends BaseApiService {
  constructor() {
    super("advertisement");
  }

  async getAllOrganizations(filters: BaseFilters) {
    const res = await this.get<BaseResponce<IOrganizations[]>>(
      `odata/SchoolAdmin?$expand=Ambassador&$expand=SchoolTag&${this.getODataQuery(filters)}`
    );

    return {
      value: res.value,
      count: res["@odata.count"],
    };
  }

  async getSchoolDivisions() {
    return await this.get<ISchoolDivision[]>("admin/school-divisions");
  }

  async addSchoolsToDivision(divisionId: string, schoolsIds: string[]) {
    return await this.post(`admin/school-divisions/${divisionId}/schools`, JSON.stringify({ SchoolsIds: schoolsIds }));
  }

  async getSchoolsByDivision(
    divisionId: string,
    filters: ISchoolDivisionFilters = {}
  ): Promise<{ value: ISchoolDivisionSchool[]; count: number }> {
    const response = await this.get<ISchoolDivisionSchool[] | BaseResponce<ISchoolDivisionSchool[]>>(
      `admin/school-divisions/${divisionId}/schools?${this.getSchoolDivisionQuery(filters)}`
    );

    if (Array.isArray(response)) {
      return { value: response, count: response.length };
    }

    const responseObject = response as BaseResponce<ISchoolDivisionSchool[]> & {
      Count?: number;
    };

    return {
      value: this.getCollectionResponse<ISchoolDivisionSchool>(response),
      count: responseObject["@odata.count"] ?? responseObject.Count ?? 0,
    };
  }

  async getAvailableSchoolsForDivision(
    divisionId: string,
    filters: ISchoolDivisionFilters = {}
  ): Promise<{ items: IAvailableSchool[]; count: number }> {
    const response = await this.get<IAvailableSchool[] | BaseResponce<IAvailableSchool[]>>(
      `admin/school-divisions/${divisionId}/available-schools?${this.getSchoolDivisionQuery(filters)}`
    );

    if (Array.isArray(response)) {
      return { items: response, count: response.length };
    }

    const responseObject = response as BaseResponce<IAvailableSchool[]> & {
      count?: number;
      Count?: number;
    };

    return {
      items: this.getCollectionResponse<IAvailableSchool>(response),
      count: responseObject["@odata.count"] ?? responseObject.count ?? responseObject.Count ?? 0,
    };
  }

  private getCollectionResponse<T>(response: unknown): T[] {
    if (Array.isArray(response)) {
      return response as T[];
    }

    if (!response || typeof response !== "object") {
      return [];
    }

    const responseObject = response as Record<string, unknown>;
    const collectionKeys = [
      "value",
      "Value",
      "data",
      "Data",
      "items",
      "Items",
      "results",
      "Results",
      "result",
      "Result",
      "collection",
      "Collection",
    ];

    for (const key of collectionKeys) {
      const collection = this.getCollectionResponse<T>(responseObject[key]);

      if (collection.length > 0 || Array.isArray(responseObject[key])) {
        return collection;
      }
    }

    return [];
  }

  async removeSchoolFromDivision(divisionId: string, schoolId: string) {
    return await this.delete(`admin/school-divisions/${divisionId}/schools/${schoolId}`);
  }

  async changeSchoolDivision(divisionId: string, schoolId: string, newDivisionId: string) {
    return await this.patch(
      `admin/school-divisions/${divisionId}/schools/${schoolId}`,
      JSON.stringify({ NewDivisionId: newDivisionId })
    );
  }

  private getSchoolDivisionQuery(filters: ISchoolDivisionFilters) {
    const query = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        query.set(key, String(value));
      }
    });

    return query.toString();
  }

  async getSchoolGatheringById(filters: BaseFilters | { MatchId: string }) {
    const res = await this.get<BaseResponce<SchoolGathering[]>>(
      `odata/SchoolGatheringMatchTicketAdmin?${this.getODataQuery({
        ...filters,
        withOutDeletedUtc: true,
      })}`
    );

    return {
      value: res.value,
      count: res["@odata.count"],
    };
  }

  async confirmSchool(id: string) {
    return await this.patch(`SchoolAdmin/Confirm?schoolId=${id}`);
  }

  async renameSchool(id: string, name: string, schoolTagId: string) {
    return this.patch(`SchoolAdmin/Update`, JSON.stringify({ SchoolId: id, Name: name, SchoolTagId: schoolTagId }));
  }

  async getSchoolTags(filter?: string) {
    const res = await this.get(
      filter ? `odata/SchoolTag?$filter=contains(Name,'${encodeURIComponent(filter)}')` : `odata/SchoolTag`
    );

    return res.value;
  }

  async createSchoolTag(name: string) {
    await this.post(`SchoolTag/Create`, JSON.stringify({ Name: name }));
  }

  async updateSchoolTag(name: string, schoolTagId: string) {
    await this.patch(`SchoolTag/Update`, JSON.stringify({ Name: name, Id: schoolTagId }));
  }

  async rejectSchool(id: string) {
    return await this.patch(`SchoolAdmin/Reject?schoolId=${id}`);
  }

  async createNewSeason(date: string | null) {
    return await this.post(`SchoolAdmin/CreateNewSeason/${date ? `?seasonStartDate=${date}` : ""}`);
  }

  async completeSeason() {
    return await this.post("SchoolAdmin/CompleteSeason");
  }

  async resumeSeason() {
    return await this.post("SchoolAdmin/ResumeSeason");
  }

  async getCurrentSeason() {
    return await this.get<ISeason>("SchoolAdmin/CurrentSeason");
  }

  async getAllTransactionsPoints(
    pointsType: "Rating" | "Activity",
    schoolId: string,
    seasonId: string,
    type: PointsType,
    filters: BaseFilters
  ) {
    const res = await this.get<BaseResponce<IPointResponse[]>>(
      `odata/School${pointsType}TransactionAdmin?${this.getODataQuery({
        ...filters,
        SchoolId: schoolId,
        SeasonId: seasonId,
        Type: type,
      })}`
    );

    return {
      value: res.value,
      count: res["@odata.count"],
    };
  }

  async addRatingPoints(data: IMutationPoints) {
    return await this.post<string, IPointResponse>("SchoolRatingTransactionAdmin/AddPoints", JSON.stringify(data));
  }

  async addActivityPoints(data: IMutationPoints) {
    return await this.post<string, IPointResponse>("SchoolActivityTransactionAdmin/AddPoints", JSON.stringify(data));
  }
  async substractActivityPoints(data: IMutationPoints) {
    return await this.post<string, IPointResponse>(
      "SchoolActivityTransactionAdmin/SubtractPoints",
      JSON.stringify(data)
    );
  }

  async removeRatingPoints(transactionId: string) {
    return await this.delete<string, IPointResponse>(
      `SchoolRatingTransactionAdmin/RemovePoints?transactionId=${transactionId}`
    );
  }
  async getAllOrganzationsShort() {
    return await this.get<{ Id: string; Name: string }[]>(`SchoolMemberClient/GetSchools`);
  }

  async updateNotificationsAmbassador(notification: IUpdateNotification) {
    return await this.patch<string, IAmbassadorOneResponse>(
      "SchoolAdmin/UpdateSchoolData",
      JSON.stringify(notification)
    );
  }

  async createLevel(level: Omit<ILevel, "Id">) {
    return await this.post("SchoolLevelAdmin/Create", JSON.stringify(level));
  }

  async updateLevel(level: ILevel) {
    return await this.put("SchoolLevelAdmin/Update", JSON.stringify(level));
  }

  async removeLevel(levelId: ILevel["Id"]) {
    return await this.delete(`SchoolLevelAdmin/Delete/${levelId}`);
  }

  async getAllLevels() {
    return await this.get<ILevel[]>("SchoolLevelAdmin/GetAll");
  }

  async getAllMatchesForApplications(filters: MatchApplicationFiltersType) {
    const res = await this.get<BaseResponce<IMatchesApplication[]>>(
      `odata/GatheringRequestMatchAdmin?$expand=HomeTeam,GuestTeam,GatherRequests&${this.getODataQuery({
        ...filters,
        withOutDeletedUtc: true,
      })}`
      // `odata/GatheringRequestMatchAdmin?$expand=HomeTeam,GuestTeam${this.getODataQuery(filters)}`
    );

    return {
      value: res.value,
      count: res["@odata.count"],
    };
  }

  async getMatchesApplicationsById(matchId: string) {
    const res = await this.get<IMatchesApplication>(
      `odata/GatheringRequestMatchAdmin(${matchId})?$expand=HomeTeam,GuestTeam`
    );

    return res;
  }

  async createMatchApplication({
    MatchId,
    GatheringEndDateUtc,
    SchoolTags,
    SchoolTickets,
  }: {
    MatchId: IMatchesApplication["MatchId"];
    GatheringEndDateUtc: IMatchesApplication["GatheringEndDateUtc"];
    SchoolTags: string[];
    SchoolTickets: SchoolTicket[];
  }) {
    return await this.post(
      "SchoolAdmin/CreateGatheringMatch",
      JSON.stringify({ MatchId, GatheringEndDateUtc, SchoolTags, SchoolTickets })
    );
  }

  async updateMatchApplication({
    MatchId,
    GatheringEndDateUtc,
    SchoolTags,
    SchoolTickets,
  }: {
    MatchId: IMatchesApplication["MatchId"];
    GatheringEndDateUtc: IMatchesApplication["GatheringEndDateUtc"];
    SchoolTags: string[];
    SchoolTickets: SchoolTicket[];
  }) {
    return await this.patch(
      "SchoolAdmin/UpdateGatheringMatch",
      JSON.stringify({ GatheringMatchId: MatchId, GatheringEndDateUtc, SchoolTags, SchoolTickets })
    );
  }

  async getMatchApplications(filters: MatchApplicationFiltersType, additionalFilters: MatchApplicationFiltersType) {
    const idFilter = additionalFilters.Id ? ` and Id eq ${additionalFilters.Id}` : "";
    const idMatch = `MatchId eq ${additionalFilters.MatchId}`;

    const withFiltersFromHelper = this.getODataQuery({
      ...filters,
      withOutDeletedUtc: true,
    }).includes("filter");

    const res = await this.get<BaseResponce<IMatchApplications[]>>(
      `odata/GatherRequestAdmin?$expand=HomeTeam,GuestTeam,Ambassador,Participants&${this.getODataQuery({
        ...filters,
        withOutDeletedUtc: true,
      })}${withFiltersFromHelper ? " and " : "&$filter="}${idMatch}${idFilter}`
    );

    return {
      value: res.value,
      count: res["@odata.count"],
    };
  }

  async getMatchApplicationsById(id: string) {
    const res = await this.get<IMatchApplications>(
      `odata/GatherRequestAdmin(${id})?$expand=HomeTeam,GuestTeam,Ambassador,Participants`
    );

    return res;
  }

  async exportGatherRequestById(requestId: string) {
    const res = await this.post(`GatherRequestAdmin/ExportReport?requestId=${requestId}&api-version=1.0`);

    return res;
  }
}

export const battleOfSchoolsRepository = new BattleOfSchoolsRepository();
